"use client";

import { useState, useEffect } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Link, usePathname } from "../i18n/routing";
import Image from "next/image";
import { FiMenu, FiX, FiChevronDown } from "react-icons/fi";
import { FaGlobe, FaBell } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import { useHolidayNotice } from "./HolidayNoticeModal";
import { GetActiveHolidayNotices } from "@/app/actions/getHolidayNotices";

// Types
type NavItem = {
  label: string;
  href: string;
  subItems?: { label: string; href: string }[];
};

export default function Header() {
  const t = useTranslations("common.menu");
  const servicesT = useTranslations("home.services");
  const pathname = usePathname();
  const locale = useLocale();
  const { showNotice } = useHolidayNotice();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const [hasHolidayNotices, setHasHolidayNotices] = useState(false);

  // Fetch holiday notices directly in the header component
  useEffect(() => {
    const checkForNotices = async () => {
      try {
        const notices = await GetActiveHolidayNotices();
        setHasHolidayNotices(notices && notices.length > 0);
        console.log("Header - Found notices:", notices ? notices.length : 0);
      } catch (error) {
        console.error("Error checking for notices:", error);
      }
    };

    checkForNotices();
  }, []);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  // Log hasNotices value to debug
  useEffect(() => {
    console.log("Header - hasHolidayNotices value:", hasHolidayNotices);
  }, [hasHolidayNotices]);

  // Create navigation items
  const navItems: NavItem[] = [
    { label: t("home"), href: "/" },
    { label: t("about"), href: "/about" },
    {
      label: t("services"),
      href: "/services",
      subItems: [
        { label: servicesT("accounting.title"), href: "/services/accounting" },
        { label: servicesT("audit.title"), href: "/services/audit" },
        {
          label: servicesT("corporateTax.title"),
          href: "/services/corporate-tax",
        },
        {
          label: servicesT("individualTax.title"),
          href: "/services/individual-tax",
        },
        {
          label: servicesT("taxPlanning.title"),
          href: "/services/tax-planning",
        },
        {
          label: servicesT("immigration.title"),
          href: "/services/immigration",
        },
      ],
    },
    { label: t("career"), href: "/career" },
    { label: t("payment"), href: "/payment" },
    { label: t("contact"), href: "/contact" },
  ];

  // Toggle submenu in mobile view
  const toggleSubmenu = (label: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenSubmenu(openSubmenu === label ? null : label);
  };

  // Check if a path is active
  const isActive = (path: string) => pathname === path;

  // Handle holiday notice click
  const handleNoticeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Header notice button clicked, calling showNotice");

    try {
      // Try the standard context approach first
      showNotice();
      console.log("showNotice function called");

      // Fallback approach - try to find and click any Dialog close buttons that might be in a hidden state
      setTimeout(() => {
        const dialogElement = document.querySelector('[role="dialog"]');
        if (
          !dialogElement ||
          dialogElement.getAttribute("data-state") !== "open"
        ) {
          console.log("Dialog not found or not open, trying direct approach");
          // Create and dispatch a custom event that the HolidayNoticeModal can listen for
          const event = new CustomEvent("openHolidayNotice");
          document.dispatchEvent(event);
        }
      }, 100);
    } catch (error) {
      console.error("Error showing notice:", error);
    }

    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="relative z-[100]">
      <header
        className={clsx(
          "fixed top-0 left-0 right-0 w-full z-[60] transition-all duration-300 overflow-visible",
          scrolled ? "bg-white shadow-md py-1" : "bg-white py-2"
        )}>
        <div className="container mx-auto px-4 overflow-visible">
          <div className="flex justify-between items-center overflow-visible">
            {/* Logo */}
            <Link href="/" className="flex items-center group">
              <div
                className={clsx(
                  "relative",
                  scrolled ? "w-12 h-12" : "w-14 h-14"
                )}>
                <Image
                  src="/logo.png"
                  alt="Aries CPA Limited"
                  width={56}
                  height={56}
                  style={{
                    width: "auto",
                    height: "100%",
                  }}
                  className="object-contain"
                  priority
                />
              </div>
              <div className="ml-3 flex flex-col group-hover:text-blue-600 transition-colors duration-150">
                <span className="text-base font-medium text-gray-800 leading-tight group-hover:text-blue-600">
                  Aries CPA Limited
                </span>
                <span className="text-xs text-gray-600 leading-tight group-hover:text-blue-600">
                  艾威斯會計師有限公司
                </span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-5">
              {navItems.map((item) => (
                <div
                  key={item.label}
                  className="relative group overflow-visible">
                  {item.subItems ? (
                    <>
                      <button
                        className="font-medium text-gray-800 hover:text-blue-600 transition-colors duration-150 flex items-center relative"
                        onClick={(e) => e.preventDefault()}>
                        {item.label}
                        <FiChevronDown className="ml-1 h-4 w-4" />
                      </button>
                      <div
                        className="
                          absolute left-0 top-full mt-1
                          w-72 min-w-max rounded-md shadow-lg 
                          bg-white border border-gray-200
                          opacity-0 invisible 
                          group-hover:opacity-100 group-hover:visible 
                          transition-all duration-150 
                          z-[500]
                        "
                        style={{
                          filter: "drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))",
                          pointerEvents: "auto",
                        }}>
                        <div className="py-2">
                          {item.subItems.map((subItem) => (
                            <Link
                              key={subItem.href}
                              href={subItem.href as any}
                              className="block px-5 py-3 text-base text-gray-700 hover:bg-gray-100 hover:text-blue-600 border-b border-gray-100 last:border-none">
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <Link
                      href={item.href as any}
                      className={clsx(
                        "font-medium transition-colors duration-150",
                        isActive(item.href)
                          ? "text-blue-600"
                          : "text-gray-800 hover:text-blue-600"
                      )}>
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
              {/* Holiday Notice Icon in Desktop */}
              {hasHolidayNotices && (
                <button
                  type="button"
                  onClick={handleNoticeClick}
                  className="inline-flex items-center justify-center p-3 bg-transparent rounded-full text-gray-800 hover:text-blue-600 focus:outline-none transition-colors duration-150 relative z-10"
                  aria-label="Show holiday notices">
                  <FaBell className="h-5 w-5" />
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-md border border-white">
                    !
                  </span>
                </button>
              )}
              {/* Language Switcher */}
              <div className="ml-5 flex items-center space-x-2 border-l pl-5 border-gray-200">
                <Link
                  href={pathname as any}
                  locale="en"
                  className={clsx(
                    "px-2 py-1 rounded text-sm",
                    locale === "en"
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "hover:bg-gray-100"
                  )}>
                  EN
                </Link>
                <Link
                  href={pathname as any}
                  locale="zh"
                  className={clsx(
                    "px-2 py-1 rounded text-sm",
                    locale === "zh"
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "hover:bg-gray-100"
                  )}>
                  繁體
                </Link>
                <Link
                  href={pathname as any}
                  locale="cn"
                  className={clsx(
                    "px-2 py-1 rounded text-sm",
                    locale === "cn"
                      ? "bg-blue-50 text-blue-600 font-medium"
                      : "hover:bg-gray-100"
                  )}>
                  简体
                </Link>
              </div>
            </nav>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              {/* Holiday Notice Button (Mobile) */}
              {hasHolidayNotices && (
                <button
                  type="button"
                  onClick={handleNoticeClick}
                  className="inline-flex items-center justify-center p-3 bg-blue-600 rounded-full text-white hover:bg-blue-700 focus:outline-none transition-colors duration-150 relative z-10"
                  aria-label="Show holiday notices">
                  <FaBell className="h-5 w-5" />
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center shadow-md border border-white">
                    !
                  </span>
                </button>
              )}

              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 focus:outline-none"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                <span className="sr-only">Open main menu</span>
                {mobileMenuOpen ? (
                  <FiX className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <FiMenu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={clsx(
            "fixed inset-0 flex z-40 lg:hidden",
            mobileMenuOpen ? "visible" : "invisible"
          )}>
          {/* Backdrop */}
          <div
            className={clsx(
              "fixed inset-0 bg-black bg-opacity-25 transition-opacity",
              mobileMenuOpen ? "opacity-100" : "opacity-0"
            )}
            aria-hidden="true"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Side drawer */}
          <div
            className={clsx(
              "relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white shadow-xl transition transform ease-in-out duration-300",
              mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
            )}>
            <div className="px-4 flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-10 w-10 relative">
                  <Image
                    src="/logo.png"
                    alt="Aries CPA Limited"
                    width={56}
                    height={56}
                    style={{
                      width: "auto",
                      height: "100%",
                    }}
                    className="object-contain"
                  />
                </div>
                <div className="ml-2 flex flex-col">
                  <span className="text-base font-medium text-gray-800 leading-tight group-hover:text-blue-600">
                    Aries CPA Limited
                  </span>
                  <span className="text-xs text-gray-600 leading-tight group-hover:text-blue-600">
                    艾威斯會計師有限公司
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none"
                onClick={() => setMobileMenuOpen(false)}>
                <span className="sr-only">Close menu</span>
                <FiX className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>

            <div className="mt-5 flex-1 h-0 overflow-y-auto">
              <nav className="px-2 space-y-1">
                {/* Holiday Notice Button (Mobile Sidebar) */}
                {hasHolidayNotices && (
                  <button
                    className={clsx(
                      "w-full flex items-center justify-start px-3 py-2 text-base font-medium rounded-md",
                      "text-blue-600 bg-blue-50 hover:bg-blue-100 mb-2"
                    )}
                    onClick={handleNoticeClick}>
                    <FaBell className="h-5 w-5 mr-2" />
                    {locale === "en"
                      ? "Holiday Notices"
                      : locale === "zh"
                      ? "假期通知"
                      : "假期通知"}
                  </button>
                )}

                {navItems.map((item) => (
                  <div key={item.label} className="py-1">
                    {item.subItems ? (
                      <>
                        <button
                          className={clsx(
                            "w-full flex justify-between items-center px-3 py-2 text-base font-medium rounded-md",
                            "text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                          )}
                          onClick={(e) => toggleSubmenu(item.label, e)}>
                          <span>{item.label}</span>
                          <FiChevronDown
                            className={clsx(
                              "h-5 w-5 transition-transform",
                              openSubmenu === item.label
                                ? "transform rotate-180"
                                : ""
                            )}
                          />
                        </button>

                        <AnimatePresence>
                          {openSubmenu === item.label && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.2 }}
                              className="ml-4 mt-1">
                              {item.subItems.map((subItem) => (
                                <Link
                                  key={subItem.href}
                                  href={subItem.href as any}
                                  className="block px-3 py-2 rounded-md text-base text-gray-600 hover:bg-gray-50 hover:text-blue-600"
                                  onClick={() => setMobileMenuOpen(false)}>
                                  {subItem.label}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        href={item.href as any}
                        className={clsx(
                          "block px-3 py-2 rounded-md text-base font-medium",
                          isActive(item.href)
                            ? "bg-blue-50 text-blue-600"
                            : "text-gray-700 hover:bg-gray-50 hover:text-blue-600"
                        )}
                        onClick={() => setMobileMenuOpen(false)}>
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>
            </div>

            {/* Language switcher in mobile menu */}
            <div className="px-4 py-4 border-t border-gray-200">
              <div className="flex items-center mb-2">
                <FaGlobe className="h-5 w-5 text-gray-500 mr-2" />
                <span className="text-gray-600">Language</span>
              </div>
              <div className="flex space-x-2 mt-2">
                <Link
                  href={pathname as any}
                  locale="en"
                  className={clsx(
                    "px-4 py-2 text-center border rounded-md flex-1",
                    locale === "en"
                      ? "bg-blue-50 text-blue-600 border-blue-200"
                      : "text-gray-700 border-gray-200 hover:bg-gray-50"
                  )}
                  onClick={() => setMobileMenuOpen(false)}>
                  English
                </Link>
                <Link
                  href={pathname as any}
                  locale="zh"
                  className={clsx(
                    "px-4 py-2 text-center border rounded-md flex-1",
                    locale === "zh"
                      ? "bg-blue-50 text-blue-600 border-blue-200"
                      : "text-gray-700 border-gray-200 hover:bg-gray-50"
                  )}
                  onClick={() => setMobileMenuOpen(false)}>
                  繁體
                </Link>
                <Link
                  href={pathname as any}
                  locale="cn"
                  className={clsx(
                    "px-4 py-2 text-center border rounded-md flex-1",
                    locale === "cn"
                      ? "bg-blue-50 text-blue-600 border-blue-200"
                      : "text-gray-700 border-gray-200 hover:bg-gray-50"
                  )}
                  onClick={() => setMobileMenuOpen(false)}>
                  简体
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>
    </div>
  );
}
